import {
  ColumnType,
  ExpectedRowTypes,
  QuestionnaireListType,
  QuestionnaireType,
  UserType,
} from "@/common/types";
import AutoComplete from "@/components/autocomplete";
import useDebounce from "@/hooks/use-debounce";
import { getAllDoctors, getAllPatients } from "@/redux/actions/usersActions";
import { RootState } from "@/redux/reducers";
import { convertDateToString } from "@/utils/date.util";
import Table from "@/components/table";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getQuestionnaires } from "@/redux/actions/questionnaireAction";
import { isUserDoctor, isUserPatient } from "@/utils/common.util";
import CardsForTable from "@/components/cards-for-table";

const columns: ColumnType[] = [
  {
    field: "test_kit_label",
    headerName: "Test Kit",
  },
  {
    field: "doctor",
    headerName: "Doctor",
    render: (row) => (row as QuestionnaireType).doctor?.name || "-",
  },
  {
    field: "patient",
    headerName: "Patient",
    render: (row) => (row as QuestionnaireType).patient?.name || "-",
  },
  {
    field: "submission_time",
    headerName: "Submission Time",
    render: (row) =>
      convertDateToString((row as QuestionnaireType).submission_time) || "-",
  },
];

const Questionnaire = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState<string>("");
  const debouncedDoctorSearchTerm = useDebounce(doctorSearchTerm, 500);
  const [patientSearchTerm, setPatientSearchTerm] = useState<string>("");
  const debouncedPatientSearchTerm = useDebounce(patientSearchTerm, 500);

  const [filterState, setFilterState] = useState({
    doctor: "",
    patient: "",
  });

  const rowsPerPage = 10;

  const { doctors, patients } = useSelector((state: RootState) => state.users);

  const { questionnaires } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: ExpectedRowTypes
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if ("id" in row) {
      navigate(`/questionnaire/${row.id}`);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelect = (key: string, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  useEffect(() => {
    dispatch(getQuestionnaires(currentPage, filterState));
  }, [currentPage, filterState]);

  useEffect(() => {
    dispatch(getAllDoctors(debouncedDoctorSearchTerm));
  }, [debouncedDoctorSearchTerm]);

  useEffect(() => {
    dispatch(getAllPatients(debouncedPatientSearchTerm));
  }, [debouncedPatientSearchTerm]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-4">
        {!isUserPatient() && (
          <div className="flex gap-2 w-full">
            {!isUserDoctor() && (
              <div className="w-[50%] md:w-[175px]">
                <AutoComplete
                  placeHolder="doctor"
                  options={doctors as UserType[]}
                  labelKey="name"
                  onSelect={(option) => handleSelect("doctor", option?.id)}
                  onChange={(search) => setDoctorSearchTerm(search)}
                  listCount={5}
                />
              </div>
            )}
            <div className="w-[50%] md:w-[175px]">
              <AutoComplete
                placeHolder="patient"
                options={patients as UserType[]}
                labelKey="name"
                onSelect={(option) => handleSelect("patient", option?.id)}
                onChange={(search) => setPatientSearchTerm(search)}
                listCount={5}
              />
            </div>
          </div>
        )}
      </div>

      <div>
        {/* Desktop View */}
        <div className="hidden md:flex items-center justify-center">
          <Table
            rows={(questionnaires as QuestionnaireListType)?.results || []}
            columns={columns}
            rowsPerPage={rowsPerPage}
            totalPages={
              Math.ceil(
                (questionnaires as QuestionnaireListType)?.count / rowsPerPage
              ) || 0
            }
            showPrevious={
              (questionnaires as QuestionnaireListType)?.previous !== null
            }
            showNext={(questionnaires as QuestionnaireListType)?.next !== null}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
            onPageChange={handlePageChange}
          />
        </div>

        {/* Mobile View */}
        <div className="flex md:hidden">
          <CardsForTable
            rows={(questionnaires as QuestionnaireListType)?.results || []}
            columns={columns}
            rowsPerPage={rowsPerPage}
            totalPages={
              Math.ceil(
                (questionnaires as QuestionnaireListType)?.count / rowsPerPage
              ) || 0
            }
            showPrevious={
              (questionnaires as QuestionnaireListType)?.previous !== null
            }
            showNext={(questionnaires as QuestionnaireListType)?.next !== null}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
